import '@blueprintjs/table/lib/css/table.css';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

const ColumnSizingHelper = ({
  headers,
  initialColumnWidths,
  minWidthAllowed = 50,
  iconColWidth = 30,
  includePostBufferCol = true,
  bufferColWidth = 30,
  shrinkToFit = true,
  hiddenColumns,
}) => {
  // This is a class from the Blueprint.js Table component that represents the width of the
  // scrollable table, not including the scrollbar (if present)
  const bpTableContainerClassName = 'bp5-table-top-container';
  const [containerWidth, setContainerWidth] = useState(0);

  const defaultColWidths = useMemo(
    () =>
      headers.map((header, index) =>
        header.isIconCol
          ? iconColWidth
          : header?.value && hiddenColumns && hiddenColumns.has(header.value)
          ? 0
          : initialColumnWidths?.length && initialColumnWidths[index]
          ? initialColumnWidths[index]
          : header.width || null,
      ),
    [headers, hiddenColumns, includePostBufferCol],
  );
  const [columnWidths, setColumnWidths] = useState([...defaultColWidths]);
  const [adjustedColumnWidths, setAdjustedColumnWidths] = useState([
    ...defaultColWidths,
  ]);

  useEffect(() => {
    // If headers are changed, make sure widths for hidden/non-hidden columns are updated
    headers?.length &&
      hiddenColumns &&
      setColumnWidths((prevWidthList) =>
        prevWidthList.map((width, index) => {
          let header = headers[index];
          let isHidden = header?.value && hiddenColumns.has(header.value);
          return isHidden ? 0 : width || defaultColWidths[index];
        }),
      );
  }, [hiddenColumns]);

  useEffect(() => {
    adjustColumnWidths();
  }, [columnWidths, containerWidth]);

  const measureContainer = () => {
    let tableContainerSize = document.getElementsByClassName(
      bpTableContainerClassName,
    )?.length
      ? document.getElementsByClassName(bpTableContainerClassName)[0]
          .clientWidth - 9
      : 1;
    setContainerWidth(tableContainerSize);
  };

  const adjustColumnWidths = () => {
    if (!containerWidth || !columnWidths?.length || !headers?.length) return;

    let cumulativeColWidths = columnWidths.reduce((sum, colWidth) => {
      sum += colWidth || 0;
      return sum;
    }, 31 + (includePostBufferCol ? bufferColWidth : 0));

    let extraWidth = containerWidth - cumulativeColWidths;
    if (!extraWidth) return;

    let numDefaultNonSpecifiedCols = defaultColWidths.filter(
      (colWidth) => colWidth === null,
    ).length;

    let numNonSpecifiedCols = columnWidths.filter(
      (colWidth) => colWidth === null,
    ).length;

    let adjWidths = [...columnWidths].map((columnWidth, index) => {
      let adjustedColWidth = columnWidth;
      let header = headers[index];

      let isHidden = hiddenColumns && hiddenColumns.has(header?.value);
      let columnIsFixed = header && (isHidden || header.isIconCol);

      let minWidthForCol = isHidden
        ? 0
        : header?.minWidth || (!columnIsFixed ? minWidthAllowed : columnWidth);

      if (numNonSpecifiedCols > 0) {
        // Some columns are not assigned a width, distribute extra width between them
        let disSizes = Math.floor(extraWidth / numNonSpecifiedCols);

        if (!columnWidth && !columnIsFixed) {
          adjustedColWidth = columnWidth + disSizes;
        }
      } else {
        if (numDefaultNonSpecifiedCols) {
          let disSizes = Math.floor(extraWidth / numDefaultNonSpecifiedCols);

          if (
            !header?.width &&
            !columnIsFixed &&
            (extraWidth > 0 || shrinkToFit)
          ) {
            adjustedColWidth = columnWidth + disSizes;
          }
        } else {
          // All columns have been assigned, stretch the last one to fill the available space
          if (extraWidth > 0) {
            if (index === headers.length - 1 && !columnIsFixed) {
              adjustedColWidth = columnWidth + extraWidth;
            }
          }
        }
      }

      return isHidden ? 0 : Math.max(adjustedColWidth, minWidthForCol);
    });

    setAdjustedColumnWidths(adjWidths);
  };

  const updateColumnWidth = (ci, newSize) => {
    let index = ci;
    let header = headers[index];
    let isHidden =
      header?.value && hiddenColumns && hiddenColumns.has(header.value);
    let newWidths = [...columnWidths];
    newWidths[index] = isHidden ? 0 : Math.max(newSize, header?.minWidth || 0);
    setColumnWidths(newWidths);
  };

  let columnWidthsForTable = [
    ...adjustedColumnWidths,
    ...(includePostBufferCol ? [bufferColWidth] : []),
  ];

  return {
    columnWidthsForTable,
    updateColumnWidth,
    measureContainer,
  };
};

ColumnSizingHelper.propTypes = {
  headers: PropTypes.array.isRequired,
  initialColumnWidths: PropTypes.array,
  minWidthAllowed: PropTypes.number,
  iconColWidth: PropTypes.number,
  includePostBufferCol: PropTypes.bool,
  bufferColWidth: PropTypes.number,
  shrinkToFit: PropTypes.bool,
  hiddenColumns: PropTypes.object,
};

export default ColumnSizingHelper;
